<script setup lang="ts">
// import type { GA4Events } from "nuxt-ga4"
import { formatMoney } from "~/utils/formatters"
import { BundleProps } from "./Bundle.props"
import { BundleProductCardProps } from "~/components/ProductPage/Utils/BundleProductCard.props"
import { addToCartPosition } from "~/utils/constants"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = withDefaults(defineProps<BundleProps>(), {
  type: "default"
})

const { addObjectToCart, trackAddToCart } = useAddObjectToCart()

const emit =
  defineEmits<{
    (e: "onAdd", products: BundleProductCardProps[]): void
  }>()

const selectedProducts = ref<BundleProductCardProps[]>([])

const totalPrice = computed(() =>
  formatMoney(
    selectedProducts.value.reduce(
      (acc, { currentPrice }) => acc + currentPrice,
      0
    )
  )
)

const totalOldPrice = computed(() =>
  formatMoney(
    selectedProducts.value.reduce((acc, { oldPrice }) => {
      if (!oldPrice) return acc
      return acc + oldPrice
    }, 0)
  )
)

const isSelected = (sku: string) =>
  selectedProducts.value.findIndex(({ productCode }) => productCode === sku) !==
  -1

const toggleProduct = (productID: string, selected: boolean) => {
  const selectedProduct: BundleProductCardProps | undefined =
    props.products.find(({ productCode }) => productCode === productID)

  if (selected && selectedProduct) {
    selectedProducts.value.push(selectedProduct)
  } else {
    selectedProducts.value = selectedProducts.value.filter(
      ({ productCode }) => productCode !== productID
    )
  }
}

const handleAddToCart = () => {
  selectedProducts.value.forEach((product, index) => {
    const gaItem = useGAEntity<typeof GA4Entities["gaItem"]>(
      `ga_item_${product.productCode}`
    )
    const { mapProduct } = useGA4SiteEvents()
    gaItem.value = mapProduct(
      product,
      index,
      VIEW_ITEM_LIST_NAMES.PRODUCT,
      VIEW_ITEM_LIST_IDS.BOUGHT_TOGETHER
    )

    addObjectToCart(product, { quantity: 1, ga4Item: gaItem.value ?? {} })
    trackAddToCart(product, {
      position: addToCartPosition.bundle,
      ga4Item: gaItem.value ?? {}
    })
  })
}
</script>

<template>
  <div
    class="
      bundle
      pig
      bg-tiffany-10
      flex flex-col
      gap-y-4
      p-4
      md:mb-8
      md:rounded-lg
    "
  >
    <h4 class="hyppo-bold">{{ $t(`productPage.bundle.${type}.title`) }}</h4>
    <p>
      <UtilsMarkdown :content="$t(`productPage.bundle.${type}.description`)" />
    </p>
    <GAListBoundleProducts
      :id="VIEW_ITEM_LIST_IDS.BOUGHT_TOGETHER"
      :name="VIEW_ITEM_LIST_NAMES.PRODUCT"
    >
      <GAItem
        :id="`bundle-${type}`"
        :item="products"
        :list-name="VIEW_ITEM_LIST_NAMES.PRODUCT"
        :list-id="VIEW_ITEM_LIST_IDS.BOUGHT_TOGETHER"
      >
        <GAViewItemList
          :name="VIEW_ITEM_LIST_NAMES.PRODUCT"
          :id="VIEW_ITEM_LIST_IDS.BOUGHT_TOGETHER"
        >
          <div
            v-if="products?.length"
            class="
              bundle__products
              bg-white
              flex flex-col
              gap-4
              overflow-auto
              rounded
              p-4
              md:flex md:flex-row
              md:justify-between
              md:px-4
              md:py-6
            "
          >
            <template v-for="product in products" :key="product.productCode">
              <ProductPageUtilsBundleProductCard
                class="md:flex-1"
                v-bind="product"
                :type="type"
                :selectable="type === 'default'"
                :selected="isSelected(product.productCode)"
                @onSelect="toggleProduct"
              />
            </template>
          </div>
        </GAViewItemList>
      </GAItem>
    </GAListBoundleProducts>
    <div
      class="
        bundle__controls
        flex flex-col
        space-y-4
        md:flex-row
        md:items-center
        md:justify-between
        md:space-y-0
      "
    >
      <div class="flex items-center gap-x-4">
        <p>
          {{ $t(`productPage.bundle.${type}.totalPrice`) }}
          <strong class="hyppo-bold text-green-main">
            {{ totalPrice }}
          </strong>
        </p>
        <small
          v-if="type === 'discount'"
          class="beaver text-black-80 line-through"
        >
          {{ totalOldPrice }}
        </small>
      </div>
      <UtilsButton
        theme="outlined-green"
        :isDisabled="!selectedProducts?.length"
        :text="$t(`productPage.bundle.${type}.addProducts`)"
        @click="handleAddToCart"
      />
    </div>
  </div>
</template>
